import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useParams } from 'react-router';
import dayjs from 'dayjs';

import { CustomAlert, CustomSwitch, CustomTable, PopupNavigation, Text } from '../../../../shared/uiComponents';
import { Tab, TabsContainer } from '../../../../shared/uiComponents/Tabs';
import { BehaviorTableBody, behaviorHeaders, behaviorHeadersBT } from './tableInfo';
import { AdminTypes } from '../../../../components/Menu';
import AddBehavior from './Actions/addBehavior';
import './index.scss';
import API from '../../../../redux/API/Main/API';
import { ClientFunderProperties } from '../../../../redux/API/ClientAPIHelpers/funding';
import { DispatchProperties, useSelector } from '../../../../redux/store';
import { getBehaviors } from '../../../../redux/State/clientSlice/behaviorReductionSlice';
import { BehaviorReductionProperties } from '../../../../redux/API/ClientAPIHelpers/behaviorReductionProperties';
import { TEXT } from '../../../../shared/Helpers/colors';
import { useScreenWidth } from '../../../../shared/Helpers/hooks';
import EditBehaviorDetails from './Actions/editBehaviorDetails';
import DeleteBehavior from './Actions/deleteBehavior';
import ArchiveComponent from './Actions/archive';
import UnarchiveComponent from './Actions/unarchive';
import GraphBehavior from './Actions/BehaviorReductionGraph/graphBehavior';
import { BrainIcon } from '../../../../shared/Assets/svgIcons';
import { DetailsContent } from './Actions/details';

const ELC_FUNDER_NAME = 'ELC';

interface BehaviorReductionProps {
	mobileHeaderButton?: boolean;
}

const BehaviorMobileCard = ({
	behavior,
	isBT,
	clientId,
	allBehaviors,
	currentIndex,
}: {
	allBehaviors?: BehaviorReductionProperties[];
	behavior: BehaviorReductionProperties;
	clientId: string;
	currentIndex?: number;
	isBT: boolean;
}) => {
	const [open, setOpen] = useState<boolean>(false);
	const [activeIndex, setActiveIndex] = useState<number>(currentIndex || 0);

	const hasNavigation = allBehaviors && allBehaviors.length > 1;
	const currentBehavior = hasNavigation ? allBehaviors[activeIndex] : behavior;

	const handlePrevious = () => {
		if (activeIndex > 0) {
			setActiveIndex(activeIndex - 1);
		}
	};

	const handleNext = () => {
		if (allBehaviors && activeIndex < allBehaviors.length - 1) {
			setActiveIndex(activeIndex + 1);
		}
	};

	const handleClose = () => {
		setOpen(false);
		setActiveIndex(currentIndex || 0);
	};

	const handleOpen = () => {
		setActiveIndex(currentIndex || 0);
		setOpen(true);
	};

	return (
		<>
			<CustomAlert
				title="Behavior details"
				titleOnTop={false}
				open={open}
				onClose={handleClose}
				Content={() => (
					<>
						<DetailsContent data={currentBehavior} />
						{hasNavigation && (
							<PopupNavigation
								currentIndex={activeIndex}
								totalCount={allBehaviors.length}
								onPrevious={handlePrevious}
								onNext={handleNext}
							/>
						)}
					</>
				)}
				Icon={BrainIcon}
			/>

			<div className="behaviorMobileCard">
				<div className="behaviorMobileCardHeader" onClick={handleOpen}>
					<Text title={behavior.name} type="psm" fontWeight="medium" color={TEXT.PRIMARY} />
				</div>
				<div className="behaviorMobileCardBody">
					<div className="behaviorMobileCardBodyItem">
						<Text title="Date created:" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
						<Text
							title={dayjs(behavior.createdAt).format('MM/DD/YYYY')}
							type="psm"
							fontWeight="medium"
							color={TEXT.SECONDARY}
						/>
					</div>
					<div className="behaviorMobileCardBodyItem">
						<Text title="Behavior type:" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
						<Text title={behavior.recordingType.text} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
					</div>
					<div className="behaviorMobileCardBodyItem">
						<Text title="Category:" type="pxs" fontWeight="regular" color={TEXT.TERTIARY} />
						<Text title={behavior.category.name} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
					</div>
				</div>
				{!isBT && (
					<div className="behaviorMobileCardFooter">
						<DeleteBehavior data={{ clientId, id: behavior.id }} onlyIcon />
						<div className="behaviorMobileCardFooterActions">
							<GraphBehavior
								data={behavior}
								behaviorId={behavior.id}
								onlyIcon
								allBehaviors={allBehaviors}
								currentIndex={currentIndex}
							/>
							{behavior.isArchived ? (
								<UnarchiveComponent data={{ clientId, id: behavior.id }} onlyIcon />
							) : (
								<ArchiveComponent data={{ clientId, id: behavior.id }} onlyIcon />
							)}
							<EditBehaviorDetails data={{ ...behavior, clientId, doNotZeroOut: false }} onlyIcon />
						</div>
					</div>
				)}
			</div>
		</>
	);
};

const BehaviorReduction: FC<BehaviorReductionProps> = ({ mobileHeaderButton }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();
	const [params, setParams] = useSearchParams();
	const includeArchived = params.get('includeArchivedBehavior') === 'true';

	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const [clientBehaviors, setClientBehaviors] = useState<BehaviorReductionProperties[]>([]);

	// Funder tabs: null = Insurance + DOE (shared tree); elcFunderId = ELC (separate tree).
	const [elcFunderId, setElcFunderId] = useState<string | null>(null);
	const activeFunderId = params.get('behaviorFunderId') || null;

	const behaviors = useSelector((state) => state.behaviorReduction.behaviors);
	const role = useSelector((state) => state.account.role);
	const isBT = role?.section.id === AdminTypes.bt;
	const loading = useSelector((state) => state.clientProgram.loading);

	const showArchivedBehaviorReductionsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		params.set('includeArchivedBehavior', `${checked}`);
		setParams(params, { replace: true });
	};

	const selectFunderTab = (funderId: string | null) => {
		if (funderId === activeFunderId) return;

		if (funderId) {
			params.set('behaviorFunderId', funderId);
		} else {
			params.delete('behaviorFunderId');
		}
		setParams(params, { replace: true });
	};

	useEffect(() => {
		if (!clientId) return undefined;

		let cancelled = false;

		API.ClientAPI.Funding.getClientFunders(clientId).then((response) => {
			if (cancelled || response?.error) return;
			const funders = response as ClientFunderProperties[];
			const elc = funders?.find((f) => f.funderName === ELC_FUNDER_NAME);
			setElcFunderId(elc?.funderId ?? null);
		});

		return () => {
			cancelled = true;
		};
	}, [clientId]);

	useEffect(() => {
		if (!clientId) return;
		dispatch(getBehaviors({ clientId, includeArchived, funderId: activeFunderId }));
	}, [clientId, includeArchived, activeFunderId, dispatch]);

	useEffect(() => {
		if (!behaviors) return;

		if (includeArchived) {
			setClientBehaviors(behaviors);

			return;
		}

		const data = behaviors.filter((item) => !item.isArchived);
		setClientBehaviors(data);
	}, [behaviors, includeArchived]);

	// If this is just rendering the mobile header button
	if (mobileHeaderButton) {
		return <AddBehavior funderId={activeFunderId} mobileButton />;
	}

	return (
		<div>
			{elcFunderId && (
				<div style={{ padding: '16px 24px 0' }}>
					<TabsContainer>
						<Tab selected={activeFunderId === null} onClick={() => selectFunderTab(null)} style={{ cursor: 'pointer' }}>
							<Text
								title="Insurance + DOE"
								type="psm"
								fontWeight={activeFunderId === null ? 'semiBold' : 'medium'}
							/>
						</Tab>
						<Tab
							selected={activeFunderId === elcFunderId}
							onClick={() => selectFunderTab(elcFunderId)}
							style={{ cursor: 'pointer' }}
						>
							<Text title="ELC" type="psm" fontWeight={activeFunderId === elcFunderId ? 'semiBold' : 'medium'} />
						</Tab>
					</TabsContainer>
				</div>
			)}
			<div className="behaviorReductionHeader">
				{!isMobile && <AddBehavior funderId={activeFunderId} />}
				<CustomSwitch
					label="Show Archived"
					checked={includeArchived}
					onChange={showArchivedBehaviorReductionsHandler}
					backgroundType="base"
					fullWidth={isMobile}
				/>
			</div>

			{isMobile ? (
				<div className="behaviorMobileCardContainer">
					{clientBehaviors.map((behavior, index) => (
						<BehaviorMobileCard
							key={behavior.id}
							behavior={behavior}
							isBT={isBT}
							clientId={clientId || ''}
							allBehaviors={clientBehaviors}
							currentIndex={index}
						/>
					))}
				</div>
			) : (
				<CustomTable
					headers={role && role.section.id !== AdminTypes.bt ? behaviorHeaders : behaviorHeadersBT}
					TableBody={BehaviorTableBody}
					data={{ query: clientBehaviors }}
					loading={loading}
					hasPagination={false}
				/>
			)}
		</div>
	);
};

export default BehaviorReduction;
